export type DescriptionChapter = {
  /** Seconds from the start of the video. */
  start: number;
  title: string;
};

// Same format YouTube uses: one `[h:]mm:ss Title` per line, anywhere in the description.
const CHAPTER_LINE = /^\s*(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\s+(\S.*?)\s*$/;

/**
 * Matches a single YouTube-style chapter line, returning its start (seconds), title and the
 * raw timestamp text as written in the description. Returns null when the line isn't a chapter.
 */
export function matchChapterLine(
  line: string,
): (DescriptionChapter & { timeLabel: string }) | null {
  const m = CHAPTER_LINE.exec(line);
  if (!m) {
    return null;
  }
  const [, h, mm, ss, title] = m;
  return {
    start: Number(h ?? 0) * 3600 + Number(mm) * 60 + Number(ss),
    title,
    timeLabel: [h, mm, ss].filter(Boolean).join(':'),
  };
}

/**
 * Extracts YouTube-style chapters from a fragment description body.
 * Lines that don't match are ignored; result is sorted by start time.
 */
export function parseDescriptionChapters(
  description: string | null | undefined,
): DescriptionChapter[] {
  if (!description) {
    return [];
  }
  return description
    .split(/\r?\n/)
    .flatMap((line) => {
      const chapter = matchChapterLine(line);
      return chapter ? [{ start: chapter.start, title: chapter.title }] : [];
    })
    .sort((a, b) => a.start - b.start);
}
